import { motion } from "framer-motion";
import {
  Smartphone,
  Calendar,
  Activity,
  Clock,
  CheckCircle,
  AlertCircle,
  User,
  MessageCircle,
} from "lucide-react";

const InstanceCard = ({ instance, onClick, onRenewClick }) => {
  const getStatusBadge = (status) => {
    switch (status) {
      case "Connected":
        return {
          wrap: "bg-[#01CC54]/10 text-[#01CC54] border-[#01CC54]/30",
          icon: <CheckCircle size={16} className="text-[#01CC54]" />,
          text: "Connected",
        };
      case "Pending":
        return {
          wrap: "bg-amber-100 text-amber-600 border-amber-300",
          icon: <Clock size={16} className="text-amber-600" />,
          text: "Pending",
        };
      case "inactive":
        return {
          wrap: "bg-red-100 text-red-600 border-red-300",
          icon: <AlertCircle size={16} className="text-red-600" />,
          text: "Inactive",
        };
      default:
        return {
          wrap: "bg-gray-100 text-gray-600 border-gray-300",
          icon: <AlertCircle size={16} className="text-gray-600" />,
          text: status?.charAt(0).toUpperCase() + status?.slice(1) || "Unknown",
        };
    }
  };

  const formatDate = (dateString) => {
    // Check if dateString is null, undefined, empty, or 0
    if (!dateString || dateString === "0" || dateString === 0) return "-";
    
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) return "-";
    
    // Check if date is the epoch (Jan 1, 1970) which indicates invalid/missing date
    if (date.getTime() === 0 || date.getFullYear() === 1970) return "-";
    
    // Return formatted date for valid dates (whether expired or not)
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };
  const badge = getStatusBadge(instance.status);

  return (
    <motion.div
      className="relative rounded-xl border border-gray-200 bg-white shadow-sm ring-1 ring-gray-100 overflow-hidden cursor-pointer group"
      whileHover={{
        scale: 1.02,
        y: -5,
      }}
      whileTap={{ scale: 0.98 }}
      onClick={() => onClick(instance)}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Top accent bar (thin, brand colors) */}
      <div className="h-1.5 bg-primary" />

      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <div className="p-2 rounded-lg mr-3 ring-1 ring-gray-200 bg-gray-50">
              <Smartphone size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 text-lg">
                {instance.name}
              </h3>
              <p className="text-sm text-gray-500">
                ID: {instance.instance_id}
              </p>
            </div>
          </div>

          {/* Status Badge */}
          <motion.div
            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${badge.wrap}`}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.15, type: "spring", stiffness: 500 }}
          >
            <span className="mr-1">{badge.icon}</span>
            {badge.text}
          </motion.div>
        </div>

        {/* Details */}
        <div className="space-y-3">
          <motion.div
            className="flex items-center text-sm text-gray-600"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.22, duration: 0.4 }}
          >
            <Calendar size={16} className="mr-2 text-gray-500" />
            <span>Created: {formatDate(instance.created_at)}</span>
          </motion.div>

          <motion.div
            className="flex items-center text-sm text-gray-600"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.26, duration: 0.4 }}
          >
            <Calendar size={16} className="mr-2 text-gray-500" />
            <span>Expiry Date: {formatDate(instance.expiryDate)}</span>
          </motion.div>

          <motion.div
            className="flex items-center text-sm text-gray-600"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.28, duration: 0.4 }}
          >
            {instance.isExpired ? (
              <AlertCircle size={16} className="mr-2 text-red-600" />
            ) : (
              <CheckCircle size={16} className="mr-2 text-[#01CC54]" />
            )}
            <span>
              {instance.isExpired ? "Expired" : "Active Subscription"}
            </span>
          </motion.div>

          {instance.status === "Connected" && (
            <>
              <motion.div
                className="flex items-center text-sm text-gray-600"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.32, duration: 0.4 }}
              >
                <MessageCircle size={16} className="mr-2 text-gray-400" />
                <span>Whatsapp Id: {instance.loggedInUserNumber?.split(':')[0] || instance.loggedInUserNumber}</span>
              </motion.div>
            </>
          )}

          <motion.div
            className="flex items-center text-sm text-gray-600"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.34, duration: 0.4 }}
          >
            <Activity size={16} className="mr-2 text-gray-400" />
            <span>
              Status: {instance.status === "Connected" ? "Connected" : "Setup Required"}
            </span>
          </motion.div>
        </div>

        {/* Actions */}
        <motion.div
          className="mt-6 pt-4 border-t border-gray-200"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.38, duration: 0.4 }}
        >
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">
              {instance.isExpired ? "Subscription expired" : "Click to manage"}
            </span>

            {instance.isExpired ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (typeof onRenewClick === "function") onRenewClick(instance);
                }}
                className="px-4 py-2 bg-primary hover:bg-primary/90 text-white text-sm font-semibold rounded-lg shadow-sm transition"
              >
                Renew Subscription
              </button>
            ) : (
              <motion.div
                className="text-primary group-hover:text-primary/80"
                animate={{ x: [0, 5, 0] }}
                transition={{
                  repeat: Infinity,
                  duration: 2,
                  ease: "easeInOut",
                  repeatDelay: 3,
                }}
              >
                <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                  <path
                    fillRule="evenodd"
                    d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"
                  />
                </svg>
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* Subtle hover overlay */}
        <motion.div
          className="pointer-events-none absolute inset-0 bg-gray-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          initial={{ opacity: 0 }}
          whileHover={{ opacity: 1 }}
        />
      </div>
    </motion.div>
  );
};

export default InstanceCard;
